// API-based services - no longer using direct Firestore
import { getAuthHeaders } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const get_dashboard_overview = async (user_id: string, user_email: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(
         `${API_BASE_URL}/api/dashboard/${user_id}?userEmail=${encodeURIComponent(user_email)}`,
         {
            method: 'GET',
            headers,
         }
      );

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch dashboard data');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      throw error;
   }
};

export const get_all_trips_and_shared_trips = async (user_id: string, user_email: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(
         `${API_BASE_URL}/api/trips/user/${user_id}?userEmail=${encodeURIComponent(user_email)}`,
         {
            method: 'GET',
            headers,
         }
      );

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch trips');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching trips:', error);
      throw error;
   }
};

export const get_all_events_and_shared_events = async (user_id: string, user_email: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(
         `${API_BASE_URL}/api/events/user/${user_id}?userEmail=${encodeURIComponent(user_email)}`,
         {
            method: 'GET',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
         }
      );

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch events');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
   }
};

export const get_user_people = async (user_id: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/people/${user_id}`, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch user people');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching user people:', error);
      throw error;
   }
};

export const get_user_trips_photos = async (user_id: string, user_email: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(
         `${API_BASE_URL}/api/photos/trips/${user_id}?userEmail=${encodeURIComponent(user_email)}`,
         {
            method: 'GET',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
         }
      );

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch trip photos');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching trip photos:', error);
      throw error;
   }
};

export const get_user_events_photos = async (user_id: string, user_email: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(
         `${API_BASE_URL}/api/photos/events/${user_id}?userEmail=${encodeURIComponent(user_email)}`,
         {
            method: 'GET',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
         }
      );

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch event photos');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching event photos:', error);
      throw error;
   }
};

export const get_user_profile_photos = async (user_id: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/photos/profile/${user_id}`, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch profile photos');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching profile photos:', error);
      throw error;
   }
};

export const get_user_photos = async (user_id: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/photos/user/${user_id}`, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch user photos');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching user photos:', error);
      throw error;
   }
};

export const get_user_transformed_photos = async (user_id: string) => {
   // This function might need a specific API endpoint if used
   // For now, keeping the original logic or implement as needed
   try {
      // Placeholder - implement API endpoint if this function is used
      console.warn('get_user_transformed_photos not yet implemented with API');
      return [];
   } catch (error) {
      console.error('Error fetching transformed photos:', error);
      throw error;
   }
};

export const get_user_profile_transformed_photo = async (user_id: string) => {
   // This function might need a specific API endpoint if used
   try {
      // Placeholder - implement API endpoint if this function is used
      console.warn('get_user_profile_transformed_photo not yet implemented with API');
      return [];
   } catch (error) {
      console.error('Error fetching profile transformed photos:', error);
      throw error;
   }
};

export const get_event_photos = async (event_id: string, user_id?: string) => {
   try {
      const headers = await getAuthHeaders();
      const queryParams = user_id ? `?userId=${user_id}` : '';
      const response = await fetch(`${API_BASE_URL}/api/events/${event_id}/photos${queryParams}`, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch event photos');
      }

      return result.photos || result; // Handle both { photos: [...] } and [...] formats
   } catch (error) {
      console.error('Error fetching event photos:', error);
      throw error;
   }
};

export const get_subscription_plan_pricing = async (email?: string) => {
   try {
      const headers = await getAuthHeaders();

      // Build query parameters - only email is needed now (Stripe direct fetch)
      const params = new URLSearchParams();
      if (email) params.append('email', email);

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/subscription-plans/pricing${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch subscription plan pricing');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching subscription plan pricing:', error);
      throw error;
   }
};

export const get_trip_photos = async (trip_id: string, user_id?: string) => {
   try {
      const headers = await getAuthHeaders();
      const queryParams = user_id ? `?userId=${user_id}` : '';
      const response = await fetch(`${API_BASE_URL}/api/trips/${trip_id}/photos${queryParams}`, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch trip photos');
      }

      return result.photos || result; // Handle both { photos: [...] } and [...] formats
   } catch (error) {
      console.error('Error fetching trip photos:', error);
      throw error;
   }
};

export const get_event_photos_by_transformed_photo = async (event_id: string) => {
   // This function might need a specific API endpoint if used
   try {
      // Placeholder - implement API endpoint if this function is used
      console.warn('get_event_photos_by_transformed_photo not yet implemented with API');
      return [];
   } catch (error) {
      console.error('Error fetching transformed event photos:', error);
      throw error;
   }
};

export const get_transformed_photos_by_photo_id = async (photo_id: string) => {
   // This function might need a specific API endpoint if used
   try {
      // Placeholder - implement API endpoint if this function is used
      console.warn('get_transformed_photos_by_photo_id not yet implemented with API');
      return [];
   } catch (error) {
      console.error('Error fetching transformed photos by photo ID:', error);
      throw error;
   }
};

export const get_trip_details = async (trip_id: string) => {
   try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/trips/${trip_id}`, {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            ...headers,
         },
      });

      const result = await response.json();

      if (!response.ok) {
         throw new Error(result.error || 'Failed to fetch trip details');
      }

      return result.data;
   } catch (error) {
      console.error('Error fetching trip details:', error);
      throw error;
   }
};
